import React from "react"
import { LifeBuoy } from "lucide-react"
import TopicPageLayout from "../topic-page-layout"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../ui/accordion"

const Troubleshooting = () => {
  const faqItems = [
    {
      question: "I can't log in to my account. What should I do?",
      answer:
        "First, try resetting your password using the 'Forgot Password' link. Make sure you're using the correct email address and check your spam folder for reset emails. If you continue to experience issues, try clearing your browser cache and cookies, or use a different browser."
    },
    {
      question: "My content won't upload. How do I fix this?",
      answer:
        "Check that your file meets our size and format requirements. For videos, we support MP4, MOV, and AVI formats up to 5GB. For images, we support JPEG, PNG, and GIF formats up to 50MB. If your file meets these requirements, try compressing it slightly or using a different browser."
    },
    {
      question: "Why hasn't my payout been processed yet?",
      answer:
        "Payouts are processed on the 15th of each month if you've reached the minimum threshold of $50. If it's after the processing date, check that your payment information is correct and complete in your dashboard. If everything looks correct, please contact support with your transaction ID."
    },
    {
      question:
        "The analytics dashboard isn't showing updated data. What's wrong?",
      answer:
        "Most analytics are updated with a slight delay of up to 24 hours. If you're not seeing data after this period, try refreshing your browser or clearing your cache. If the issue persists, check our system status page for any reported delays or outages."
    },
    {
      question:
        "My subscribers can't see my exclusive content. How do I fix this?",
      answer:
        "First, check the content settings to ensure it's properly set as visible to the correct tier of subscribers. Next, ask subscribers to refresh their page or clear their cache. If the problem persists, verify that their subscription payments have processed successfully."
    }
  ]

  return (
    <TopicPageLayout
      title="Troubleshooting"
      description="Quick fixes for common issues you might encounter on our platform."
      icon={<LifeBuoy className="h-8 w-8 text-[#3D2A60]" />}
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Common Issues & Solutions
          </h2>
          <p className="text-gray-600 mb-6">
            Before contacting support, try these solutions for common problems
            encountered by creators. Many issues can be quickly resolved
            without waiting for support.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Login & Account Access
          </h2>
          <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
              Forgotten Password
            </h3>
            <ol className="list-decimal pl-5 space-y-2 text-gray-600">
              <li>Click the "Forgot Password" link on the login page</li>
              <li>Enter the email address associated with your account</li>
              <li>
                Check your inbox (and spam folder) for a password reset link
              </li>
              <li>Follow the link to create a new password</li>
              <li>Try logging in with your new credentials</li>
            </ol>
          </div>

          <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6">
            <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
              Account Locked
            </h3>
            <p className="text-gray-600 mb-4">
              Accounts may be temporarily locked after multiple failed login
              attempts as a security measure. To unlock your account:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-gray-600">
              <li>Wait 30 minutes before trying to log in again</li>
              <li>Use the "Forgot Password" option to reset your password</li>
              <li>Check your email for verification requests</li>
              <li>
                If you still can't access your account after 24 hours, contact
                support
              </li>
            </ol>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Content Upload Issues
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1 bg-white shadow-sm border border-gray-100 rounded-lg p-6">
                <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
                  File Size & Format
                </h3>
                <p className="text-gray-600 mb-3">
                  Make sure your content meets our requirements:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>
                    <span className="font-medium">Videos:</span> MP4, MOV, AVI
                    (max 5GB)
                  </li>
                  <li>
                    <span className="font-medium">Images:</span> JPEG, PNG,
                    GIF (max 50MB)
                  </li>
                  <li>
                    <span className="font-medium">Audio:</span> MP3, WAV (max
                    300MB)
                  </li>
                  <li>
                    <span className="font-medium">Documents:</span> PDF, DOCX
                    (max 100MB)
                  </li>
                </ul>
              </div>

              <div className="flex-1 bg-white shadow-sm border border-gray-100 rounded-lg p-6">
                <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
                  Upload Failures
                </h3>
                <p className="text-gray-600 mb-3">
                  If uploads consistently fail:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Try a different browser</li>
                  <li>Check your internet connection stability</li>
                  <li>Compress your file slightly</li>
                  <li>Clear your browser cache</li>
                  <li>Try uploading in smaller batches</li>
                </ul>
              </div>
            </div>

            <div className="bg-[#FCF1F6] p-6 rounded-lg">
              <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                Pro Tip
              </h3>
              <p className="text-gray-700">
                For large video files, try encoding them using H.264 codec and
                reducing the bitrate slightly. This can dramatically reduce
                file size with minimal quality loss and make uploads more
                reliable.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Payment & Payout Issues
          </h2>
          <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6">
            <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
              Delayed Payouts
            </h3>
            <p className="text-gray-600 mb-4">
              If your payout is delayed beyond the expected processing time:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-gray-600">
              <li>
                Verify that you've reached the minimum payout threshold ($50)
              </li>
              <li>
                Check that your payment information is complete and correct
              </li>
              <li>
                Confirm that you've completed all required tax documentation
              </li>
              <li>
                Check if your account is under review (this will be indicated
                in your dashboard)
              </li>
              <li>
                Allow 3-5 business days after the processing date for funds to
                appear
              </li>
            </ol>

            <div className="mt-4 p-4 border-l-4 border-[#E563A4] bg-[#FCF1F6]/30">
              <p className="text-gray-700">
                <span className="font-medium">Note:</span> First-time payouts
                may take 5-7 business days as they require additional
                verification. Subsequent payouts are typically faster.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-[#3D2A60]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-gray-600">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section>
          <div className="bg-[#F4F0FA] p-6 rounded-lg text-center">
            <h2 className="text-xl font-semibold mb-3 text-[#3D2A60]">
              Still Need Help?
            </h2>
            <p className="text-gray-700 mb-4">
              If you're still experiencing issues after trying these
              solutions, our support team is here to assist you.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/helpdesk"
                className="inline-flex items-center justify-center px-5 py-3 bg-[#3D2A60] text-white rounded-md hover:bg-[#2A1C42] transition-colors"
              >
                Return to Help Center
              </a>
              <a
                href="/help/contact-support"
                className="inline-flex items-center justify-center px-5 py-3 bg-white border border-[#3D2A60] text-[#3D2A60] rounded-md hover:bg-[#F4F0FA] transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </section>
      </div>
    </TopicPageLayout>
  )
}

export default Troubleshooting
